import { FirebaseAuthTypes, getIdToken, reload } from '@react-native-firebase/auth';
import * as Sentry from '@sentry/react-native';
import axios, { AxiosError, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';
import { getIdToken as getIdTokenWeb, reload as reloadWeb, User } from 'firebase/auth';
import { Platform } from 'react-native';

import { API_URL } from '@/constants/config';
import { auth } from '@/firebaseConfig';

export const client = axios.create({
  baseURL: API_URL,
  headers: {
    'content-type': 'application/json'
  },
  withCredentials: true
});

// AxiosRetry configuration
axiosRetry(client, {
  retries: 1,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    return axiosRetry.isNetworkError(error) || error.response?.status === 409 || error.status === 409;
  },
  onRetry: async (_retryCount, _error, requestConfig) => {
    try {
      const newToken = await refreshToken(auth().currentUser);
      if (newToken) {
        setAuthHeader(newToken);
        requestConfig.headers = {
          ...requestConfig.headers,
          Authorization: `Bearer ${newToken}`
        };
      } else {
        // Logout if we can't get a new token
        await auth().signOut();
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  }
});

// Helpers
const refreshToken = async (user: FirebaseAuthTypes.User | User | null): Promise<string | null> => {
  if (!user) return null;

  if (Platform.OS === 'web') {
    await reloadWeb(user as User);
    return await getIdTokenWeb(user as User);
  } else {
    await reload(user as FirebaseAuthTypes.User);
    return await getIdToken(user as FirebaseAuthTypes.User);
  }
};

const setAuthHeader = (token: string) => {
  client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

// API Interceptor
client.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    Sentry.captureException(error);
    return Promise.reject(error);
  }
);
